import React, { useLayoutEffect } from "react";
import { motion } from "framer-motion";
import RevampHero from "./RevampHero";
import RevampHeroFollow from "./RevampHeroFollow";
import RevampExploreFeed from "./RevampExploreFeed";
import "./revamp.css";

function RevampHome() {
  const fadeUp = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  useLayoutEffect(() => {
    const container = document.querySelector(".revamp-page");
    if (!container) return undefined;

    const headingSelectors = [
      "h1",
      "h2",
      ".revamp-hero-heading",
      ".revamp-hiw-title",
      ".revamp-feed-title",
    ].join(", ");

    const headings = Array.from(container.querySelectorAll(headingSelectors));
    if (!headings.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      headings.forEach((heading) => heading.classList.add("revamp-heading-in-view"));
      return undefined;
    }

    const originals = new Map();
    const animateClass = "revamp-heading-letter";
    const splitTextNodes = (node, indexRef) => {
      const childNodes = Array.from(node.childNodes);
      childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent || "";
          if (!text.trim()) return;

          const fragment = document.createDocumentFragment();
          // Group letters into per-word wrappers so the per-letter
          // inline-block spans can only break at spaces, never mid-word.
          text.split(/(\s+)/).forEach((token) => {
            if (token === "") return;
            if (/^\s+$/.test(token)) {
              fragment.appendChild(document.createTextNode(token));
              return;
            }
            const word = document.createElement("span");
            word.className = "revamp-heading-word";
            Array.from(token).forEach((char) => {
              const span = document.createElement("span");
              span.className = animateClass;
              span.style.setProperty("--revamp-letter-delay", `${indexRef.value * 18}ms`);
              span.textContent = char;
              word.appendChild(span);
              indexRef.value += 1;
            });
            fragment.appendChild(word);
          });
          child.replaceWith(fragment);
          return;
        }

        if (child.nodeType === Node.ELEMENT_NODE) {
          splitTextNodes(child, indexRef);
        }
      });
    };

    headings.forEach((heading) => {
      originals.set(heading, heading.innerHTML);
      heading.classList.add("revamp-heading-animate");
      splitTextNodes(heading, { value: 0 });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("revamp-heading-in-view");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -4%" }
    );

    headings.forEach((heading) => observer.observe(heading));

    return () => {
      observer.disconnect();
      headings.forEach((heading) => {
        const original = originals.get(heading);
        if (original) heading.innerHTML = original;
      });
    };
  }, []);

  return (
    <div className="revamp-page">
      <main className="revamp-home-main">
        <motion.div
          className="revamp-focus-section revamp-focus-hero"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <RevampHero />
        </motion.div>
        <motion.div
          className="revamp-focus-section revamp-focus-follow"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <RevampHeroFollow />
        </motion.div>
        <motion.div
          className="revamp-focus-section revamp-focus-explore revamp-explore-bridge"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <RevampExploreFeed />
        </motion.div>
      </main>
    </div>
  );
}

export default RevampHome;
